'use client';

import { PropsWithChildren, useEffect, useState, useTransition } from 'react';
import { useSession } from 'next-auth/react';
import { useSWRConfig } from 'swr';
import { type UserProfileDto } from '@/authApiClient';
import { PostLoginFlow } from './PostLoginFlow';
import { PostSignUpFlow } from './PostSignUpFlow';
import { PrivateContextProvider } from './PrivateContext';
import {
  useDatadogCorrelation,
  useInitializeContext,
  useRoleRedirect,
  usePrivateContextState,
  useDatadogUser,
} from '@/hooks';
import { useLaunchDarklyUser } from '@/hooks/useLaunchDarklyUser';
import { CircularProgress } from '@/primitives';

interface PrivateProps extends PropsWithChildren {
  onRenderPublicSearchStateChange?: (shouldRenderPublicSearch: boolean) => void;
}

export function Private({
  children,
  onRenderPublicSearchStateChange = () => {},
}: PrivateProps) {
  const { data: session, status } = useSession({ required: true });
  const { mutate } = useSWRConfig();
  const [loadedUserProfile, setLoadedUserProfile] = useState<UserProfileDto>();

  const {
    userOrganisations,
    setUserOrganisations,
    selectedOrganisationId,
    setSelectedOrganisationId,
    setShowSplashScreen,
    contextData,
    setContextData,
  } = usePrivateContextState();

  const [isPending] = useTransition();

  // When session loads, populate context with bootstrap data
  useEffect(() => {
    if (status === 'authenticated' && session?.user) {
      setContextData((prevData) => ({
        ...prevData,
        userId: session.user.internalUserId,
        userGuid: session.user.userGuid,
        userProfileGuid: session.user.userProfileGuid,
        email: session.user.email,
        entitlements: session.user.entitlements,
      }));
    }
  }, [status, session, setContextData]);

  useDatadogCorrelation();
  useInitializeContext(
    selectedOrganisationId,
    userOrganisations,
    setContextData,
    setUserOrganisations,
    setSelectedOrganisationId,
  );
  useDatadogUser(loadedUserProfile, contextData, selectedOrganisationId);
  useLaunchDarklyUser();

  const handleUserProfileLoaded = (userProfile: UserProfileDto) => {
    setLoadedUserProfile(userProfile);
    setContextData((prevContextData) => ({
      ...prevContextData,
      userProfile,
      reloadUserProfile: () => {
        mutate(`/api/UserProfile/${prevContextData?.userId}`);
      },
    }));
  };

  const redirectByRole = useRoleRedirect(
    selectedOrganisationId,
    onRenderPublicSearchStateChange,
  );

  useEffect(() => {
    if (!isPending) {
      setShowSplashScreen(false);
    }
  }, [isPending, setShowSplashScreen]);

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <CircularProgress />
      </div>
    );
  }

  return (
    <PrivateContextProvider value={contextData}>
      <PostLoginFlow
        onUserProfileLoaded={handleUserProfileLoaded}
        onUserOrgAssociationsRetrieved={redirectByRole}
      />
      <PostSignUpFlow onFinishFlow={redirectByRole} />
      {children}
    </PrivateContextProvider>
  );
}
